import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { SyncStatus } from "./sync_status"

export default class SubscriptionBridgeController extends BridgeComponent {
  static component = "subscription"
  static values = {
    subscribed: Boolean,
    trialDaysRemaining: Number,
    trialExpired: Boolean,
    syncStatus: String,
  }

  declare readonly subscribedValue: boolean
  declare readonly trialDaysRemainingValue: number
  declare readonly trialExpiredValue: boolean
  declare syncStatusValue: string

  connect(): void {
    super.connect()

    if (this.enabled) {
      this.sendStatus()
      this.element.addEventListener("bridge:reply", this.handleBridgeReply as EventListener)
    }
  }

  disconnect(): void {
    this.element.removeEventListener("bridge:reply", this.handleBridgeReply as EventListener)
    super.disconnect()
  }

  sendStatus(): void {
    this.send("status", {
      subscribed: this.subscribedValue,
      trialDaysRemaining: this.trialDaysRemainingValue,
      trialExpired: this.trialExpiredValue,
      sync_status: this.syncStatusValue,
    })
  }

  // MARK: - Native purchase actions

  purchase({ params }: { params: { productId: string } }): void {
    this.send("purchase", { product_id: params.productId })
  }

  restore(): void {
    this.send("restore", {})
  }

  manage(): void {
    this.send("manage", {})
  }

  // MARK: - Bridge reply handler

  private handleBridgeReply = (event: CustomEvent): void => {
    const message = event.detail

    if (message.event === "sync") {
      const { transaction_id, product_id } = message.data || {}
      if (transaction_id && product_id) {
        this.updateSyncStatus(SyncStatus.Syncing)
        this.performFetch(transaction_id, product_id)
      } else {
        this.updateSyncStatus(SyncStatus.Deferred)
      }
      return
    }

    if (["purchase", "restore", "refresh"].includes(message.event)) {
      const newStatus = message.data?.sync_status
      if (newStatus && newStatus !== this.syncStatusValue) {
        this.updateSyncStatus(newStatus)
      }
    }
  }

  // MARK: - Status updates

  private updateSyncStatus(status: string): void {
    if (status === this.syncStatusValue) return
    this.syncStatusValue = status
    this.element.dispatchEvent(
      new CustomEvent("subscription:changed", {
        bubbles: true,
        detail: { syncStatus: status },
      })
    )
  }

  // MARK: - Server sync

  private async performFetch(transactionId: string, productId: string): Promise<void> {
    try {
      const response = await fetch("/api/subscription/sync", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          platform: "ios",
          transaction_id: transactionId,
          product_id: productId,
        }),
      })

      if (response.ok) {
        this.updateSyncStatus(SyncStatus.None)
        this.reportSyncResult("success", transactionId, productId)
        return
      }

      const body = await response.json().catch(() => ({}))

      if (response.status === 202) {
        this.updateSyncStatus(SyncStatus.Deferred)
        this.reportSyncResult("error", transactionId, productId, "Sync deferred")
        return
      }

      this.updateSyncStatus(SyncStatus.None)
      this.reportSyncResult(
        "error",
        transactionId,
        productId,
        body.error || `Sync failed with status ${response.status}`
      )
    } catch (error) {
      this.updateSyncStatus(SyncStatus.None)
      this.reportSyncResult(
        "error",
        transactionId,
        productId,
        error instanceof Error ? error.message : "Unknown error"
      )
    }
  }

  /**
   * Reports the result of a server sync back to native so it can
   * manage the pending queue (remove on success, enqueue on error).
   */
  private reportSyncResult(
    status: "success" | "error",
    transactionId: string,
    productId: string,
    error?: string
  ): void {
    const payload: Record<string, string> = {
      status,
      transaction_id: transactionId,
      product_id: productId,
    }
    if (error) {
      payload.error = error
    }
    this.send("sync-result", payload)
  }
}
